(ns ductile.ui.vehicle-search
  (:require ["@radix-ui/react-dropdown-menu" :as DropdownMenu]
            [clojure.edn :as edn]
            [clojure.set :as set]
            [clojure.string :as str]
            [ductile.base64u :as base64]
            [ductile.clerk.viewer :as dcv]
            [ductile.compound-config :as compound-config]
            [ductile.time.format :as time-format]
            [ductile.ui.commands :as commands]
            [ductile.ui.components :as ui]
            [ductile.ui.components.columns :as columns]
            [ductile.ui.components.combobox :as combobox]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.fields :as fields]
            [ductile.ui.http :as http]
            [ductile.ui.icons :as icons]
            [ductile.ui.locale :as locale :refer [tr trn]]
            [ductile.ui.router :as router]
            [ductile.ui.util :as util]
            [ductile.ui.vehicle-call-off :as vehicle-call-off]
            [ductile.util :refer [if-ok-let]]
            [nextjournal.clerk.render :as render]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clerk.viewer :as viewer]
            [reagent.core :as r]))

(def max-query-length
  "Thresold until which the search params will be reflected in the url to
  support reloading & sharing."
  3000)

(def default-result-limit 100)

(def !results
  (deframe/!route-get ::search-results))

(def !selected-rows
  (deframe/!route-get ::selected-rows))

(def !col-settings
  (deframe/!localstorage-get :ductile.ui.vehicle-search/col-settings))

(def !selected-profile-by-use-case
  (deframe/!localstorage-get :ductile.ui.vehicle-search/selected-profile-by-use-case {}))

(defn selected-row-ductile-ids [{:keys [table num]} selected-rows-set]
  (let [{:keys [columns rows]} table
        select-count (count selected-rows-set)]
    (cond
      (= num select-count) ;; everything is selected
      {}
      (zero? select-count) ;; nothing is selected
      nil
      :else
      ;; compute :include/:exclude ids
      (let [largest-selected-row (reduce max 0 selected-rows-set)
            last-visible-row (count rows)
            [direction row-set] (if (> largest-selected-row last-visible-row)
                                  [:excludes (remove (partial contains? selected-rows-set)
                                                     (range 0 last-visible-row))]
                                  [:includes selected-rows-set])
            ductile-id-index (get (zipmap columns (range (count columns)))
                                  :ductile/id)
            row-index->ductile-id (into {}
                                        (map-indexed (fn [idx row] [idx (nth row ductile-id-index)]))
                                        rows)
            ductile-ids (into #{} (vals (select-keys row-index->ductile-id row-set)))]
        (if (= :excludes direction)
          {:ductile.vehicle-search/not ductile-ids}
          ductile-ids)))))

(defn results-elided?
  "check if we there's more results than we're showing"
  [results]
  (not= (:num results) (-> results :table :rows count)))

(defn full-vin? [s]
  (boolean (re-matches #"^[A-Za-z0-9]{17}$" s)))

(defn partial-vin? [s]
  (boolean (re-matches #"[A-Za-z0-9]{7,17}" s)))

(defn hiccup->text
  [node]
  (cond
    (string? node) node
    (vector? node) (apply str (filter string? (tree-seq vector? rest node)))))

(defn selected-row-vins [{:as results :keys [table num]} selected-rows-set]
  (when (results-elided? results)
    (throw (ex-info "cannot compute selected-row-vins because we have elided results" (select-keys results [:num]))))
  (let [vin-index (get (zipmap (:columns table) (range (count (:columns table))))
                       :vehicle/vin)
        row-index->vin (into {}
                             (map-indexed (fn [idx row]
                                            [idx (hiccup->text (last (:nextjournal/value (nth row vin-index))))]))
                             (:rows table))
        vins (mapv row-index->vin (sort selected-rows-set))]
    (when-not (every? full-vin? vins)
      (throw (ex-info "cannot find vins to copy" {:vins vins})))
    vins))

(comment
  (selected-row-vins @(deframe/!route-get-in [::search-results :result]) @!selected-rows))

(defn without-empty-dates [m]
  (if (map? m)
    (let [{:ductile.vehicle-search/keys [on before after]} m]
      (cond-> m
        (str/blank? on)
        (dissoc :ductile.vehicle-search/on)
        (str/blank? before)
        (dissoc :ductile.vehicle-search/before)
        (str/blank? after)
        (dissoc :ductile.vehicle-search/after)))
    m))

(defn prepare-search-params [search-params]
  (update-vals search-params without-empty-dates))

(def !form-params (deframe/!route-get ::form-params))

(comment @!form-params)

(defn search-params-with-row-selection
  "Builds vehicle search params respecting the active row selection and the possible use-case"
  []
  (merge (select-keys (:path-params @deframe/!current-route) [:compound/name])
         @!form-params
         {:ductile/id (selected-row-ductile-ids (:result @!results) @!selected-rows)}))

(defn continue-to-call-off-button []
  [ui/button {:variant :primary
              :data-testid "continue-to-call-off-button"
              :disabled? (zero? (count @!selected-rows))
              :on-click (fn [e]
                          (.stopPropagation e)
                          (vehicle-call-off/continue! (search-params-with-row-selection)))}
   (tr "Continue to Call-Off")
   [ui/icon :arrow-right]])

(defn kia-switch-to-passive-custody-button []
  [:div.flex.items-center
   [ui/button
    {:variant :primary
     :disabled (zero? (count @!selected-rows))
     :on-click (fn [e]
                 (.stopPropagation e)
                 (when (js/confirm (tr "Are you sure you want switch %1 cars to passive custody?" (count @!selected-rows)))
                   (-> (http/$lambda 'ductile.workflow.engine/handle-kia-passive-custody-request
                                     (search-params-with-row-selection))
                       (.then
                        (fn [result]
                          (if (:error result)
                            (deframe/route-assoc! ::flash-notice
                                                  {:type :error :message (:error-message result)})
                            (do
                              (deframe/route-assoc! ::flash-notice
                                                    {:type :info
                                                     :message (tr "Switched %1 vehicle(s) to Passive Custody" (count (:passive-custody-vins result)))})
                              (deframe/dispatch! `search (merge (select-keys @deframe/!current-route [:path-params])
                                                                {:form-params @!form-params})))))))))}

    (tr "Switch to Passive Custody")]])


(defn available-entry-modes []
  (-> (compound-config/get-compound-config (util/current-compound-name))
      :compound/entry-modes
      (set/intersection #{:mode/train :mode/ship})
      sort))

(def !create-entry-transport-state
  (r/track (fn []
             (let [modes (available-entry-modes)
                   default-mode (when (= 1 (count modes))
                                  (first modes))
                   {:as form :keys [action]} (merge {:action :create
                                                     :transport/mode default-mode}
                                                    @(deframe/!route-get :create-entry-transport))
                   assumed-vehicles-count (when-let [v (:transport/assumed-vehicles-count form)]
                                            (parse-long v))]
               (cond-> (dissoc form :transport/assumed-vehicles-count)
                 (and (= action :use-existing)
                      (not (contains? form :transport/lookup-ref))
                      (first (:result @deframe/!current-route-fetch)))
                 (assoc :transport/lookup-ref [:ductile/id (:ductile/id (first (:result @deframe/!current-route-fetch)))])
                 assumed-vehicles-count
                 (assoc :transport/assumed-vehicles-count assumed-vehicles-count)
                 true
                 (select-keys (case action
                                :use-existing [:action :transport/lookup-ref]
                                :create [:action :transport/name :transport/mode :transport/assumed-vehicles-count])))))))

(def !create-exit-transport-state
  (r/track (fn []
             (let [{:as form :keys [action]} (merge {:action :create
                                                     :transport/mode :mode/truck}
                                                    @(deframe/!route-get :create-exit-transport))]
               (cond-> form
                 (and (= action :use-existing)
                      (not (contains? form :transport/lookup-ref))
                      (first (:result @deframe/!current-route-fetch)))
                 (assoc :transport/lookup-ref [:ductile/id (:ductile/id (first (:result @deframe/!current-route-fetch)))])
                 true
                 (select-keys (case action
                                :use-existing [:action :transport/lookup-ref]
                                :create (concat [:action :transport/name :transport/mode]
                                                (when (= :mode/truck (:transport/mode form))
                                                  [:transport/transporter])))))))))

(comment
  @!create-exit-transport-state)

(defn entry-transport-form [{:as args :keys [on-submit]}]
  (let [{:as params :keys [action]} @!create-entry-transport-state
        submit! (fn [event]
                  (.preventDefault event)
                  (on-submit params))
        transports @(deframe/!get-in [:transports :result] [])]

    [:<>
     [:form
      [:div.flex.items-center {:class ["px-3" "py-2"]}
       [:div.flex-shrink-0.pr-1 {:class "w-1/2"}
        [:div.font-bold.mb-1 {:class "text-[12px]"} (tr "Entry transport")]
        [ui/tabs {:value action
                  :on-value-change #(deframe/route-assoc-in! [:create-entry-transport :action] (keyword %))}
         [ui/tabs-list
          [ui/tab {:size 1 :value :create :data-testid "entry-transport-create"} (tr "New")]
          [ui/tab {:size 1 :value :use-existing :data-testid "entry-transport-use-existing"} (tr "add to existing")]]]]
       (when (= :create action)
         [:div.flex-shrink-0.pl-1 {:class "w-1/2"}
          [:div.font-bold.mb-1 {:class "text-[12px]"} (tr "Transport mode")]
          [ui/select {:on-change #(deframe/route-assoc-in! [:create-entry-transport :transport/mode] %)
                      :placeholder (str (tr "Select transport mode") "…")
                      :value (:transport/mode params)
                      :data-testid "entry-transport-mode"
                      :choices (map (fn [mode]
                                      {:ui/value mode
                                       :ui/label (tr mode)
                                       :data-testid (str "entry-transport-mode-" (name mode))})
                                    (available-entry-modes))}]])]
      (cond
        (= :create action)
        [:div
         [:div.flex.items-center {:class ["px-3" "py-0"]}
          [:div.flex-shrink-0.pr-1 {:class "w-1/2"}
           [:div.font-bold.mb-1 {:class "text-[12px]"} (tr "Transport name")]
           [ui/input
            {:type "text"
             :value (:transport/name params)
             :placeholder (tr "Transport name")
             :on-change #(deframe/route-assoc-in! [:create-entry-transport :transport/name] (.. % -target -value))
             :class "h-[28px] text-[12px]"
             :data-testid "entry-transport-name"}]]
          [:div.flex-shrink-0.pr-1 {:class "w-1/2"}
           [:div.font-bold.mb-1 {:class "text-[12px]"} (tr :transport/assumed-vehicles-count)]
           [ui/input
            {:type "text"
             :value (:transport/assumed-vehicles-count params)
             :placeholder (tr :transport/assumed-vehicles-count)
             :on-change #(deframe/route-assoc-in! [:create-entry-transport :transport/assumed-vehicles-count] (.. % -target -value))
             :class "h-[28px] text-[12px]"
             :data-testid "entry-transport-assumed-vehicles-count"}]]]
         [:div
          {:class ["px-3" "pt-2" "pb-2"]}
          [ui/button
           {:size 1
            :variant :primary
            :on-click submit!
            :data-testid "entry-transport-submit"}
           (tr "Create transport")]]]
        (= :use-existing action)
        [:div
         [:div.text-xs
          {:class ["px-3" "pb-2"]}
          (cond
            @(deframe/!get-in [:transports :loading])
            [ui/loading-indicator-with-label (str (tr "Loading transports") "…")]
            (seq transports)
            [:<>
             [combobox/view {:placeholder (str (tr "Select transport") "…")
                             :choices (map (fn [transport] {:ui/label (ui/transport-label transport)
                                                            :ui/value (:ductile/id transport)})
                                           transports)
                             :value (second (:transport/lookup-ref params))
                             :on-change #(deframe/route-assoc-in!
                                          [:create-entry-transport :transport/lookup-ref]
                                          [:ductile/id (:ui/value %)])}]

             [:div.flex.items-center.justify-end.gap-3.pt-2
              [ui/button {:size 1
                          :variant :primary
                          :on-click submit!}
               (tr "Add to transport")]]]
            :else
            [:div.text-slate11 {:class "text-[12px]"}
             [:span.font-medium (tr "No transports available to select yet.")] [:br]
             (tr "You have to create a transport first.")])]])]]))

(defn create-entry-transport-form []
  (let [!error (hooks/use-state nil)]
    (hooks/use-effect
     (fn []
       (deframe/call-lambda
        {:result-path [:transports]}
        'ductile.transport/entry-transports-thin
        (deframe/namespace-path-params (:path-params @deframe/!current-route)))))
    [:<>
     (when-let [error @!error]
       [:div.overflow-x-auto.p-2.bg-red-50.rounded-md.border.border-red-400.mb-2.flex-shrink-0
        [ui/expected-error-view error]])
     [:div.mb-3.bg-slate-100.rounded-lg.border.border-slate-300
      {:class "max-w-[550px]"}
      [entry-transport-form
       {:on-submit (fn [params]
                     (-> (http/$lambda 'ductile.transport/handle-assign-entry-transport
                                       (assoc (select-keys @!create-entry-transport-state
                                                           (case (:action @!create-entry-transport-state)
                                                             :use-existing [:transport/lookup-ref]
                                                             :create [:transport/mode :transport/name :transport/assumed-vehicles-count]))
                                              :compound/name (get-in @deframe/!current-route [:path-params :compound/name])
                                              :vehicle-search-params {:search-params (search-params-with-row-selection)
                                                                      :basis-t (get-in @!results [:result :basis-t])}))
                         (.then (fn [{:as result :keys [error]}]
                                  (if error
                                    (reset! !error result)
                                    (router/navigate-to (router/path '[compound :compound/name transports :ductile/id]
                                                                     (merge (:path-params @deframe/!current-route)
                                                                            result))))))
                         (.catch (fn [error] (reset! !error error)))))}]]]))

(defn exit-transport-form [{:keys [on-submit trucks loads]}]
  (let [{:as params :keys [action]} @!create-exit-transport-state
        submit! (fn [event]
                  (.preventDefault event)
                  (on-submit params))]
    [:<>
     [:form
      [:div.flex.items-center {:class ["px-3" "py-2"]}
       [:div.flex-shrink-0.pr-1 {:class "w-1/2"}
        [:div.font-bold.mb-1 {:class "text-[12px]"} (tr "Exit transport")]
        [ui/tabs {:value action
                  :on-value-change #(deframe/route-assoc-in! [:create-exit-transport :action] (keyword %))}
         [ui/tabs-list
          [ui/tab {:size 1 :value :create :data-testid "exit-transport-create"} (tr "New")]
          [ui/tab {:size 1 :value :use-existing :data-testid "exit-transport-use-existing"} (tr "add to existing")]]]]
       (when (= :create action)
         [:div.flex-shrink-0.pl-1 {:class "w-1/2"}
          [:div.font-bold.mb-1 {:class "text-[12px]"} (tr "Transport mode")]
          [ui/select {:on-change #(deframe/route-assoc-in! [:create-exit-transport :transport/mode] %)
                      :placeholder (str (tr "Select transport mode") "…")
                      :value (:transport/mode params)
                      :data-testid "exit-transport-mode"
                      :choices (map (fn [mode]
                                      {:ui/value mode
                                       :ui/label (tr mode)
                                       :data-testid (str "exit-transport-mode-" (name mode))})
                                    [:mode/truck :mode/train :mode/ship :mode/own-wheels])}]])]
      (cond
        (= :create action)
        [:div
         [:div.flex.items-center {:class ["px-3" "py-0"]}
          [:div.flex-shrink-0.pr-1 {:class "w-1/2"}
           [:div.font-bold.mb-1 {:class "text-[12px]"} (tr "Transport name")]
           [ui/input
            {:type "text"
             :value (:transport/name params)
             :placeholder (tr "Transport name")
             :on-change #(deframe/route-assoc-in! [:create-exit-transport :transport/name] (.. % -target -value))
             :class "h-[28px] text-[12px]"
             :data-testid "exit-transport-name"}]]
          (when (= :mode/truck (:transport/mode params))
            [:div.flex-shrink-0.pl-1 {:class "w-1/2"}
             [:div.font-bold.mb-1.flex.items-center.gap-1
              {:class "text-[12px]"}
              (tr "Truck number")]
             [combobox/view
              {:size 2
               :data-testid "truck-selection"
               :variant :flex
               :placeholder (tr "Truck number (optional)")
               :value (:transport/transporter params)
               :choices (map (fn [truck] {:ui/value [:ductile/id (:ductile/id truck)]
                                          :ui/label (:truck/number truck)}) trucks)
               :on-clear #(deframe/route-dissoc-in! [:create-exit-transport :transport/transporter])
               :on-change (fn [{truck-lookup-ref :ui/value}]
                            (deframe/route-assoc-in! [:create-exit-transport :transport/transporter] truck-lookup-ref))}]])]
         [:div
          {:class ["px-3" "pt-2" "pb-2"]}
          (when (= :mode/truck (:transport/mode params))
            [:div.text-slate11.leading-normal.mb-2 {:class "text-[11px]"}
             (tr "You can create trucks or edit existing trucks on the")
             " "
             [ui/link
              {:href "/trucks"}
              (tr "Trucks")]
             " "
             (tr "Page") "."])
          [ui/button
           {:size 1
            :variant :primary
            :on-click submit!
            :data-testid "exit-transport-submit"}
           (tr "Create tour")]]]
        (= :use-existing action)
        [:div
         [:div.text-xs
          {:class ["px-3" "pb-2"]}
          (cond
            @(deframe/!get-in [:transports :loading])
            [ui/loading-indicator-with-label (str (tr "Loading transports") "…")]
            (seq loads)
            [:<>
             [combobox/view {:placeholder (str (tr "Select transport") "…")
                             :choices (map (fn [transport] {:ui/label (ui/transport-label transport)
                                                            :ui/value (:ductile/id transport)})
                                           loads)
                             :value (second (:transport/lookup-ref params))
                             :on-change #(deframe/route-assoc-in!
                                          [:create-exit-transport :transport/lookup-ref]
                                          [:ductile/id (:ui/value %)])
                             :data-testid "select-existing-load"}]
             [:div.flex.items-center.justify-end.gap-3.pt-2
              [ui/button {:size 1
                          :variant :primary
                          :on-click submit!}
               (tr "Add to transport")]]]
            :else
            [:div.text-slate11 {:class "text-[12px]"}
             [:span.font-medium (tr "No transports available to select yet.")] [:br]
             (tr "You have to create a transport first.")])]])]]))

(defn create-exit-transport-form []
  (let [!error (hooks/use-state nil)]
    (hooks/use-effect
     (fn []
       (deframe/call-lambda {:result-path [:trucks]} 'ductile.transport/active-trucks {})
       (deframe/call-lambda {:result-path [:loads]}
                            'ductile.transport/compound-loads-thin
                            (deframe/namespace-path-params (:path-params @deframe/!current-route)))))
    [:<>
     (when-let [error @!error]
       [:div.overflow-x-auto.p-2.bg-red-50.rounded-md.border.border-red-400.mb-2.flex-shrink-0
        [ui/expected-error-view error]])
     [:div.mb-3.bg-slate-100.rounded-lg.border.border-slate-300
      {:class "max-w-[550px]"}
      [exit-transport-form
       {:trucks @(deframe/!get-in [:trucks :result] [])
        :loads @(deframe/!get-in [:loads :result] [])
        :on-submit (fn [params]
                     (-> (http/$lambda 'ductile.transport/handle-upsert-exit-transport
                                       (-> (select-keys @!create-exit-transport-state
                                                        (case (:action @!create-exit-transport-state)
                                                          :use-existing [:transport/lookup-ref]
                                                          :create [:transport/mode :transport/name :transport/transporter]))
                                           (merge (deframe/current-route-params))
                                           (assoc :vehicle-search-params {:search-params (search-params-with-row-selection)
                                                                          :basis-t (get-in @!results [:result :basis-t])})))
                         (.then (fn [{:as result :keys [error]}]
                                  (if error
                                    (reset! !error result)
                                    (router/navigate-to (router/path '[compound :compound/name transports :ductile/id]
                                                                     (merge (:path-params @deframe/!current-route)
                                                                            result))))))
                         (.catch (fn [error] (reset! !error error)))))}]]]))

(def !use-case (r/track (fn [] @(deframe/!route-get ::use-case))))
(comment @!use-case)

(defn decode-set-params
  [params k]
  (let [v (get params k)]
    (cond
      (nil? v) params
      (set? v) params
      (string? v) (assoc params k (set (str/split v ",")))
      :else params)))

(defn update-when [m k f & args]
  (if (contains? m k)
    (apply update m k f args)
    m))

(defn decode-selected-columns-params
  [params]
  (update-when params :selected-columns (fn [cols] (if (string? cols)
                                                     (->> (str/split cols ",")
                                                          (map keyword)
                                                          (into (sorted-set)))
                                                     cols))))

(defn decode-vin-query-params
  [{:as params :keys [vin vins vin-regex]}]
  (cond
    vin (-> params
            (dissoc :vin)
            (assoc :vehicle/vin vin))
    vins (-> params
             (dissoc :vins)
             (assoc :vehicle/vin (into (sorted-set) (str/split vins ","))))
    vin-regex (-> params
                  (dissoc :vin-regex)
                  (assoc :vehicle/vin {:ductile.vehicle-search/regex vin-regex}))
    :else params))

(defn query-params->url!
  {::deframe/effect true}
  [params]
  (let [query-string (when (seq params) (str "?b64=" (base64/encode (pr-str params))))]
    (if (< max-query-length (count query-string))
      (js/console.log (str "skipping `ductile.ui.vehicle-search/query-params->url!` query string because query string would be too long (" (count query-string) " characters)"))
      (.replaceState js/window.history nil nil (or query-string (.-pathname js/location))))))

(def disabled-columns
  (into [:ductile/id :vehicle/height-mm :vehicle/width-mm :vehicle/length-mm
         :entry-transport-order/origin-location-ductile-id
         :entry-transport-order/destination-location-ductile-id
         :exit-transport-order/origin-location-ductile-id
         :exit-transport-order/destination-location-ductile-id
         :workflow/active-service-codes
         :workflow/extra-services
         :workflow/open-service-codes
         :workflow/services
         :exit-transport-order/edi-instructions
         :entry-transport-order/edi-instructions
         :fahrzeug/vehicle
         :fahrzeug-destination/ductile-id :fahrzeug-destination/external.kia-location-codes :fahrzeug-destination/external.bmw-location-codes
         :fahrzeug-destination/external.omoda-jaecoo-location-codes :fahrzeug-destination/location-types
         :entry-transport/ductile-id :exit-transport/ductile-id :entry-transport-order/ductile-id :exit-transport-order/ductile-id
         :workflow/open-service-codes-not-blocking-dispo
         :workflow/scheduled-services]))

(defn set-on-load-search-params!
  "side-step for passing search params that exceed the supported url length.
   Workaround for https://github.com/nextjournal/ductile/issues/2336"
  [search-params]
  (deframe/assoc! ::on-load-search-params search-params))

(defn parse-query-params [query-params]
  (if-let [b64 (:b64 query-params)]
    (-> (edn/read-string (base64/decode b64))
        (update-when :selected-columns (fn [cols] (if (set? cols)
                                                    (filterv cols locale/default-column-order)
                                                    cols)))
        (decode-set-params :service/code)
        (decode-set-params :contract/name)
        (decode-set-params :customer/name)
        (decode-set-params :make/name))
    (-> query-params
        (decode-selected-columns-params)
        (util/parse-query-params #{:vin :vins :vin-regex :customer/name :make/name :compound/name})
        (decode-vin-query-params)
        (decode-set-params :service/code)
        (decode-set-params :contract/name)
        (decode-set-params :customer/name)
        (decode-set-params :make/name))))

(def default-columns
  #{:vehicle/vin
    :vehicle/model
    :contract/name
    :customer/name
    :entry/datetime
    :ars/id
    :dispo/flag
    :dispo/available-datetime
    :entry-transport-order/destination-location-address-postcode
    :entry-transport-order/destination-location-address-city
    :entry-transport-order/destination-location-address-name
    :vehicle/blocks
    :vehicle/dimensions
    :vehicle/weight
    :vehicle/engine-type
    :vehicle/new?
    :color/description})

(defn effective-col-settings
  "Returns column settings with the selected profile for the current use case.
   Returns nil if no profiles are saved (so default-selected-cols is used)."
  []
  (let [col-settings @!col-settings]
    ;; Only return col-settings if there are saved profiles
    (when (seq (::columns/profiles col-settings))
      (let [use-case-key (:use-case-key @!use-case)
            profile-id (get @!selected-profile-by-use-case use-case-key :default-profile)]
        (assoc col-settings ::columns/selected-profile-id profile-id)))))

(defn available-columns
  "Returns the set of columns that can be selected (excluding disabled columns)."
  [result-columns]
  (apply disj (set result-columns) disabled-columns))

(defn default-selected-cols-ordered
  "Returns the default selected columns in the order they appear in the server response.
   This preserves the natural column order when creating a new profile."
  [result-columns]
  (let [selected (set (or (:selected-columns @!use-case) default-columns))]
    (filterv selected result-columns)))

(defn on-col-settings-change!
  "Persists column settings and updates the per-use-case profile selection."
  [new-col-settings]
  (deframe/localstorage-assoc! :ductile.ui.vehicle-search/col-settings new-col-settings)
  (let [use-case-key (:use-case-key @!use-case)
        profile-id (::columns/selected-profile-id new-col-settings)]
    (deframe/localstorage-assoc!
     :ductile.ui.vehicle-search/selected-profile-by-use-case
     (assoc @!selected-profile-by-use-case use-case-key profile-id))))

(defn selected-columns-from-profile
  "Extracts selected column keys from the current profile settings.
   Returns nil if no profiles are saved (to preserve existing behavior)."
  [result-columns]
  (when-let [col-settings (effective-col-settings)]
    (let [opts (columns/->table-opts {:col-settings col-settings
                                      :available-cols (available-columns result-columns)
                                      :default-selected-cols (default-selected-cols-ordered result-columns)})]
      (seq (:select-columns opts)))))

(defn selected-columns []
  (seq (or @(deframe/!route-get ::selected-columns)
           (:selected-columns @!form-params)
           (when-let [result-columns (-> @!results :result :table :columns)]
             (selected-columns-from-profile result-columns))
           (:selected-columns @!use-case)
           default-columns)))

(def !selected-columns
  (r/track selected-columns))

(defn debug-query [option-values]
  (let [!el (hooks/use-ref nil)
        !height (hooks/use-state nil)]
    (hooks/use-effect (fn []
                        (reset! !height (.-offsetHeight @!el)))
                      [@!form-params])
    [:<>
     [:div.flex-shrink-0 {:style {:height (or @!height 0)}}]
     [:div.fixed.pl-5.pr-1.pb-5.bottom-0
      {:class "left-[3px] w-full max-w-[570px]"
       :ref !el}
      [:div.bg-white.border.border-slate-300.p-3.rounded-lg.overflow-x-auto
       [render/inspect-presented {:!expanded-at (r/atom {[] true})} (viewer/present @!form-params)]
       #_[render/inspect-presented {:!expanded-at (r/atom {[] true})} (viewer/present option-values)]
       #_[render/inspect-presented {:!expanded-at (r/atom {[] true})} (viewer/present @ui/!field-state)]]]]))

(defn show-field? [k]
  (let [{:keys [only]} @!use-case]
    (or (and (nil? only) (not= :fahrzeug/on-compound k))
        (contains? only k))))

(defn type->options [option-values k type]
  (case type
    :datetime fields/default-datetime-presence-options
    :default fields/default-presence-options
    :text [fields/ignored-attr fields/missing-attr (fields/present-text-attr {:input-mask :ui.mask/license-plate})]
    :multi-select [fields/ignored-attr
                   fields/missing-attr
                   (fields/present-multi-select-attr (get option-values k))
                   (fields/exclude-multi-select-attr (get option-values k))]
    :parking [fields/ignored-attr fields/missing-attr (fields/present-parking-attr k option-values)]
    :transport [fields/ignored-attr fields/missing-attr (fields/present-transport-attr k option-values)]))

(defn search
  {::deframe/event true}
  [{:keys [db]} {:as _opts :keys [form-params path-params]}]
  (let [use-case (get-in db [:current-route/transient ::use-case])]
    {::deframe/call-lambda
     [{:result-path (deframe/route-path ::search-results)
       :on-result (fn [result]
                    (when-not (:error result)
                      (deframe/route-assoc! ::selected-rows (if (:rows-preselected? use-case true)
                                                              (set (range 0 (:num result)))
                                                              #{}))))}
      'ductile.vehicle-search/use-case-search
      {:limit default-result-limit
       :ductile.vehicle-search/use-case (:use-case-key use-case)
       :search-params (dissoc (prepare-search-params (merge form-params
                                                            path-params))
                              :selected-columns)}]
     `query-params->url! [(prepare-search-params form-params)]}))

(defn commit-value
  {::deframe/event true}
  [{:keys [db]} v]
  (if (= v (get-in db [:current-route/transient ::form-params]))
    {}
    {:db (deframe/process-tx db [[:op/assoc-in [:current-route/transient ::form-params] v]])
     :dispatch [`search (merge
                         (select-keys @deframe/!current-route [:path-params])
                         {:form-params v})]}))

(defn commit-value! [_ k v]
  (let [m @!form-params]
    (deframe/dispatch! `commit-value (cond
                                       (str/blank? v) (dissoc m k)
                                       (vector? k) (assoc-in m k v)
                                       :else (assoc m k v)))))

(def use-case-data
  {:create-entry-transport {:form create-entry-transport-form}
   :create-exit-transport {:form create-exit-transport-form}
   :call-off {:action continue-to-call-off-button}
   :claimsecur/insurance-portal {:action #()}
   :kia/customer-portal {:action #()}
   :passive-custody {:action kia-switch-to-passive-custody-button}
   :inventory {:fields {:fahrzeug/on-compound {:options (remove #{fields/ignored-attr
                                                                  fields/missing-attr
                                                                  fields/present-attr}
                                                                fields/default-datetime-presence-options)}}}
   :exit-report {:fields {:exit/datetime {:options (remove #{fields/ignored-attr
                                                             fields/missing-attr}
                                                           fields/default-datetime-presence-options)}}}
   :entry-report {:fields {:entry/datetime {:options (remove #{fields/ignored-attr
                                                               fields/missing-attr}
                                                             fields/default-datetime-presence-options)}}}})

(defn section [{:as search-opts :keys [option-values]} title & fields]
  (into [fields/section title]
        (->> fields
             (filter (fn [[k _v]] (show-field? k)))
             (map (fn [[k v]]
                    [fields/list-select !form-params k (-> v
                                                           (assoc :options (or (get-in use-case-data [(:use-case-key @!use-case) :fields k :options])
                                                                               (type->options option-values
                                                                                              (get v :option-values/reuse-from k)
                                                                                              (:type v))))
                                                           (assoc :commit-fn commit-value!)
                                                           (dissoc :type))])))))

(defn vec-without-index [v i]
  (when v
    (into (subvec v 0 i) (subvec v (inc i)))))

(defn token-button [!state i token]
  (let [!el (hooks/use-ref nil)
        {:keys [selected-token-index]} @!state
        selected? (= i selected-token-index)]
    (hooks/use-effect #(when selected? (.focus @!el)) [@!state])
    [:div.pl-3.pr-2.py-1.text-xs.font-mono.border.border-slate-300.shadow-sm.flex.items-center.gap-1.rounded-md.border.justify-between
     {:class (if selected? "bg-indigo-600 text-white " "bg-indigo-100 ")
      :ref !el
      :tabIndex 0}
     [:div.flex.items-center
      [:span.opacity-70.mr-2 (inc i) "."]
      [:span token]]
     [:button
      {:on-click (fn [event]
                   (swap! !state (fn [{:as state :keys [tokens]}]
                                   (assoc state :tokens (vec-without-index tokens i)))))}
      [ui/icon {:size 16} :x]]]))

(defn use-token-selection [!state]
  (hooks/use-effect
   (fn []
     (let [key-down-handler (fn [event]
                              (.stopPropagation event)
                              (let [{:keys [tokens selected-token-index !input tokens-selectable?]} @!state
                                    input-focused? (= @!input js/document.activeElement)
                                    last-index (dec (count tokens))]
                                (when tokens-selectable?
                                  (when (= (.-key event) "Escape")
                                    (.blur @!input)
                                    (ui/quit-editing-field!))
                                  (when (= (.-key event) "ArrowUp")
                                    (cond
                                      (not input-focused?)
                                      (do (.preventDefault event)
                                          (swap! !state assoc :selected-token-index (max 0 (dec selected-token-index))))

                                      (and input-focused? (zero? (.-selectionStart @!input)))
                                      (swap! !state assoc :selected-token-index last-index)))
                                  (when (= (.-key event) "ArrowDown")
                                    (cond
                                      (and (not input-focused?) (< selected-token-index last-index))
                                      (do (.preventDefault event)
                                          (swap! !state assoc :selected-token-index (inc selected-token-index)))

                                      (and (not input-focused?) (= selected-token-index last-index))
                                      (do (swap! !state dissoc :selected-token-index)
                                          (.focus @!input))))
                                  (when (= (.-key event) "Backspace")
                                    (cond
                                      (and input-focused? (str/blank? (.-value @!input)))
                                      (swap! !state assoc :selected-token-index last-index)

                                      (and (not input-focused?) selected-token-index)
                                      (do (swap! !state (fn [state]
                                                          (let [updated-tokens (vec-without-index tokens selected-token-index)]
                                                            (cond-> (assoc state :tokens updated-tokens)
                                                              (empty? updated-tokens)
                                                              (dissoc :selected-token-index)

                                                              (< (dec (count updated-tokens)) selected-token-index)
                                                              (assoc :selected-token-index (dec (count updated-tokens)))))))
                                          (when (empty? (:tokens @!state)) (.focus @!input))))))))]
       (js/addEventListener "keydown" key-down-handler)
       #(js/removeEventListener "keydown" key-down-handler)))))

(defn token-input [!state {:keys [on-focus on-paste on-input placeholder]}]
  (let [!input (hooks/use-ref nil)
        {:keys [query selected-token-index tokens]} @!state]
    (hooks/use-effect #(swap! !state assoc :!input !input) [@!input])
    (hooks/use-effect #(set! (.-value @!input) "") [tokens])
    (use-token-selection !state)
    [:div.flex.flex-col.gap-2
     {:on-click #(.focus @!input)}
     (let [max-vins 10]
       [:<>
        (into [:<>]
              (map-indexed (fn [i token] [token-button !state i token]))
              (take max-vins tokens))
        (when-let [rest (not-empty (drop max-vins tokens))]
          (into [:<> [:div "..."]]
                (map-indexed (fn [i token] [token-button !state (dec (count tokens)) token]))
                [(last tokens)]))])
     [:input.text-sm.px-3.py-2.shadow.border.border-slate-300.rounded-md
      {:ref !input
       :default-value ""
       :placeholder placeholder
       :on-focus (fn [event]
                   (on-focus event)
                   (swap! !state dissoc :selected-token-index))
       :on-paste on-paste
       :on-input on-input}]]))

(defn vin-list [!state]
  (let [{:keys [selected-vin-index tokens !input query vins]} @!state]
    (hooks/use-effect
     (fn []
       (let [key-down-handler (fn [event]
                                (let [{:keys [selected-vin-index vins tokens]} @!state]
                                  (when (= "ArrowDown" (.-key event))
                                    (.stopPropagation event)
                                    (swap! !state assoc :selected-vin-index (min (inc selected-vin-index) (count vins))))
                                  (when (= "ArrowUp" (.-key event))
                                    (.stopPropagation event)
                                    (swap! !state assoc :selected-vin-index (max 0 (dec selected-vin-index))))
                                  (when (= "Enter" (.-key event))
                                    (.stopPropagation event)
                                    (.preventDefault event)
                                    (swap! !state (fn [state]
                                                    (-> state
                                                        (assoc :tokens (vec (conj tokens (:vehicle/vin (nth vins selected-vin-index)))))
                                                        (dissoc :query :vins))))
                                    (.focus @!input))))]
         (js/addEventListener "keydown" key-down-handler)
         #(js/removeEventListener "keydown" key-down-handler))))
    (into [:div]
          (map-indexed
           (fn [i {:as vehicle :keys [vehicle/model vehicle/vin]}]
             (let [selected? (= selected-vin-index i)]
               [:div.p-1.text-sm.flex.items-center.gap-3
                {:data-testid "vin-completion"
                 :class (if selected?
                          "bg-indigo-600 text-white rounded-md"
                          "hover:bg-indigo-50 border-b")
                 :on-click (fn [event]
                             (.preventDefault event)
                             (.stopPropagation event)
                             (swap! !state (fn [state]
                                             (-> state (assoc :tokens (vec (conj tokens vin))) (dissoc :query :vins))))
                             (.focus @!input))}
                [:div.rounded.flex.justify-center.items-center.bg-white.border.border-gray-300.shadow-sm.p-1
                 {:style {:width 44 :height 44}}
                 [ui/make-logo (:make/name vehicle)]]
                [:div.flex-auto
                 [:div.text-sm.monospace (commands/highlight-query query vin)]
                 [:div.text-xs
                  {:class (if selected? "text-white" "text-slate-600")}
                  model]]])))
          vins)))

(defn vin-token-field [!state !m k opts]
  (let [{:keys [editing? vins query tokens !input]} @!state
        commit! (:commit-value! opts commit-value!)]
    (hooks/use-effect #(when tokens
                         (commit! !m k (if (empty? tokens) {} (set tokens))))
                      [tokens])
    (hooks/use-effect #(swap! !state assoc :tokens-selectable? (and editing? (not vins))) [vins editing?])
    [:div.text-sm.relative
     [token-input !state
      {:placeholder (tr "Search VINs")
       :data-testid "vin-field-search"
       :on-focus #(when-not editing? (ui/edit-field! (:label opts)))
       :on-paste (:on-paste opts)
       :on-input (fn [event]
                   (let [query (.. event -target -value)]
                     (if (< 2 (count query))
                       (-> (http/$vehicle-search
                            {:ductile.vehicle-search/use-case :vin-search
                             :search-params (cond-> {:vehicle/vin {:ductile.vehicle-search/regex (str "(?i)" query)}}
                                              (util/current-compound-name)
                                              (assoc :compound/name (util/current-compound-name)))
                             :limit 10})
                           (.then #(swap! !state assoc :query query :vins % :selected-vin-index 0))
                           (.catch #(swap! !state assoc :error % :query query :vins nil)))
                       (swap! !state assoc :query query :vins nil :selected-vin-index nil))))}]
     (if (seq vins)
       [:div.mt-2
        [vin-list !state]]
       (when-not (str/blank? query)
         [:div.px-3.py-1.text-xs.text-red-600.mt-1.flex.gap-1
          (tr "Couldn’t find any VINs for \"%1\"" query)]))]))

(defn free-form-vin->regex [vins-str]
  (let [vins (not-empty (->> (str/split vins-str #"[\s,]") (remove str/blank?) (filter partial-vin?)))]
    (cond (every? full-vin? vins)
          {:full-vins (into #{} vins)
           :ductile.vehicle-search/recognized-vins vins}
          vins
          {:ductile.vehicle-search/regex (str "(?i)(" (str/join "|" vins) ")$")
           :ductile.vehicle-search/recognized-vins vins}
          :else
          {:error ::invalid-vin-string
           :vins-string vins-str})))

(defn commit-vin-search! [!m k !state]
  (let [{:keys [input-text tab]} @!state
        commit! (:commit-value! @!state commit-value!)]
    (case tab
      :free-form (if-ok-let [parse-result (free-form-vin->regex input-text)]
                   (do (swap! !state #(-> %
                                          (dissoc :parsing-error)
                                          (assoc :recognized-vins (:ductile.vehicle-search/recognized-vins parse-result))))
                       (commit! !m k (or (:full-vins parse-result)
                                         (select-keys parse-result [:ductile.vehicle-search/regex]))))
                   (swap! !state #(-> % (assoc :parsing-error parse-result) (dissoc :recognized-vins))))
      :regex (commit! !m k {:ductile.vehicle-search/regex input-text})
      nil)))

(defn vin-search [!m k opts]
  (let [!state (ui/use-field-state (:label opts) (cond-> {}
                                                   (:commit-value! opts)
                                                   (assoc :commit-value! (:commit-value! opts))))
        !input (hooks/use-ref nil)
        {:keys [editing? focused? tab paste] :or {tab :per-vin}} @!state
        v (get @!m k)]
    (hooks/use-effect
     (fn []
       (swap! !state #(cond
                        (map? v) (assoc % :tab :regex :input-text (:ductile.vehicle-search/regex v))
                        (string? v) (assoc % :tab :free-form :input-text v)
                        :else (assoc % :tab :per-vin :tokens (when v (vec v))))))
     [(nil? tab)])
    (hooks/use-effect #(when (and tab editing?)
                         (when-let [input (or @!input (some-> @!state :!input deref))]
                           (.focus input))) [tab editing?])
    [fields/box
     {:show-editable? false
      :editing? editing?
      :focused? focused?
      :editing-rounded-class "rounded-lg"}
     [:div
      [fields/label (:label opts) (when (ui/debug-mode?) [fields/debug-label k])]
      [:div.mt-1
       (let [on-click #(if editing?
                         (swap! !state dissoc :query)
                         (ui/edit-field! (:label opts)))]
         [:div.flex
          [fields/tabs !state :tab [{:label (tr "Search VIN")  :tab :per-vin :on-click on-click}
                                    {:label (tr "Paste VINs") :tab :free-form :on-click on-click}
                                    {:label (tr "Regex") :tab :regex :on-click on-click}]]])]]
     [:div.w-full.mt-2
      (case tab
        :per-vin [vin-token-field !state !m k opts]
        :free-form [:div.flex.flex-col
                    [:textarea.rounded-md.p-2.text-sm.w-full.resize-none.text-black.border.shadow
                     {:ref !input
                      :data-testid "vin-field-paste"
                      :on-key-down (fn [event]
                                     (cond
                                       (= "Escape" (.-key event))
                                       (do (.blur @!input)
                                           (ui/quit-editing-field!))
                                       (and (= "Enter" (.-key event)) (not (.-shiftKey event)))
                                       (do (.preventDefault event)
                                           (commit-vin-search! !m k !state))))
                      :class "min-h-[50px]"
                      :placeholder (tr "Paste VINs (last 7-17 digits) from the clipboard.")
                      :default-value (or (and (string? v) v) "")
                      :on-input #(swap! !state assoc :input-text (.. % -target -value))
                      :on-focus #(when-not editing? (ui/edit-field! (:label opts)))}]]
        :regex [:input.text-sm.px-3.py-2.shadow.border.border-slate-300.rounded-md.w-full
                {:ref !input
                 :default-value (or (:ductile.vehicle-search/regex v) "")
                 :placeholder (tr "Regex")
                 :on-focus #(when-not editing? (ui/edit-field! (:label opts)))
                 :on-input #(swap! !state assoc :input-text (.. % -target -value))
                 :on-key-down (fn [event]
                                (case (.-key event)
                                  "Escape" (do (.blur @!input) (ui/quit-editing-field!))
                                  "Enter" (commit-vin-search! !m k !state)
                                  nil))}])
      [:div.flex.justify-between.gap-3
       (when (= tab :free-form)
         [:<>
          (when-let [parsing-error (:parsing-error @!state)]
            [:div.text-red-600.text-xs.mt-2 {:class "pt-[1px]"}
             (tr "Must contain 7-17 digit VINs")])
          (when-let [vins (:recognized-vins @!state)]
            [:div.text-xs.text-green-600.mt-2 {:class "pt-[1px]"}
             (tr "%1 VINs recognized" (count vins))])])
       [:div.flex.items-center.justify-end.gap-4.text-xs.mt-2
        (when (= tab :free-form)
          [:div.flex.items-center.gap-2
           [:div.relative {:class "top-[0px]"} [ui/keys-hint "⇧" "↩"]]
           [fields/hint (tr "to insert new line")]])
        [:div.flex.items-center.gap-2
         [:div.relative {:class "top-[0px]"} [ui/keys-hint "↩"]]
         [fields/hint (tr "to search")]]]]]
     (when (ui/debug-mode?)
       [:div.bg-white.px-3.py-1.text-slate-900.overflow-x-auto.shadow-inner.border.rounded.mt-3
        [render/inspect-presented {:!expanded-at (r/atom {[] true})} (viewer/present @!state)]])]))

(defn search-form [{:as search-opts :keys [option-values]}]
  (ui/use-focusable-fields)
  [:div
   [:div.flex.items-center.mb-3.w-full.gap-3
    {:class "min-h-[42px]"}
    [:h2.text-lg.mt-0.pl-1
     (:title search-opts (tr "Vehicle search"))]
    [ui/button {:variant :primary
                :on-click (fn []
                            ;; TODO: Next step - get rid of cursor and move vin search state into deframe
                            (let [!state (r/cursor ui/!field-state [:labels->fields (tr "VINs")])]
                              (commit-vin-search! !form-params :vehicle/vin !state)))}
     [ui/icon :magnifying-glass]
     (tr "Search")]]
   [:div.overflow-auto.flex.flex-col.gap-2.mt-2
    {:style {:max-height "calc(100vh - 130px - 22px)"} :class "px-[3px] py-[2px]"}
    [vin-search !form-params :vehicle/vin {:label (tr "VINs")}]
    [section search-opts (tr "Vehicle Status")
     [:entry/expected-datetime {:label (tr "Expected") :type :datetime}]
     [:entry/datetime {:label (tr "Entry") :type :datetime}]
     [:exit/expected-datetime {:label (tr "Expected exit") :type :datetime}]
     [:exit/datetime {:label (tr "Exit") :type :datetime}]
     [:fahrzeug/on-compound {:label (tr "On Compound") :type :datetime}]
     [:fahrzeug/delivery-dealer-code-raw {:label (tr "Delivery dealer code") :type :multi-select}]
     [:fahrzeug/expected-customer-delivery-datetime {:label (tr "Expected customer delivery") :type :datetime}]
     [:vehicle/blocks {:label (tr "Blocked") :type :multi-select}]
     [:fahrzeug/damages {:label (tr "Damaged") :type :default}]
     [:fahrzeug/customs-number {:label (tr "Customs number") :type :default}]
     [:fahrzeug/parking-spot {:label (tr "Parked at") :type :parking}]]
    [section search-opts (tr "Vehicle details")
     [:make/name {:label (tr "Make") :type :multi-select}]
     [:vehicle/model {:label (tr "Model") :type :multi-select}]
     [:vehicle/engine-type {:label (tr "Engine type") :type :multi-select}]
     [:vehicle/license-plate {:label (tr :vehicle/license-plate) :type :text}]
     [:contract/name {:label (tr "Contract") :type :multi-select}]
     [:contract/name-or-parent {:label (tr "Contract (including subcontracts)") :type :multi-select :option-values/reuse-from :contract/name}]
     [:customer/name {:label (tr "Customer") :type :multi-select}]
     [:service/code {:label (tr "Service") :type :multi-select}]]
    [section search-opts (tr "Transport")
     [:entry/transport-order {:label (tr :entry/transport-order) :type :transport}]
     [:entry/transport {:label (tr :entry/transport) :type :transport}]
     [:exit/transport-order {:label (tr :exit/transport-order) :type :transport}]
     [:exit/transport {:label (tr :exit/transport) :type :transport}]]
    (when (ui/debug-mode?)
      [debug-query option-values])]])

(defn head-checkbox [row-count _index]
  (let [!el (hooks/use-ref nil)]
    (hooks/use-effect
     (fn []
       (cond
         (= (count @!selected-rows) row-count) (do (set! (.-indeterminate @!el) false)
                                                   (set! (.-checked @!el) true))
         (empty? @!selected-rows) (do (set! (.-indeterminate @!el) false)
                                      (set! (.-checked @!el) false))
         :else (set! (.-indeterminate @!el) true)))
     [@!selected-rows])
    [:input {:type "checkbox"
             :ref !el
             :data-testid "toggle-all"
             :on-input #(deframe/route-assoc! ::selected-rows (if (empty? @!selected-rows)
                                                                (set (range 0 row-count))
                                                                #{}))}]))

(defn row-checkbox [{:keys [idx vin]}]
  (let [checked? (some? @(deframe/!route-get-in [::selected-rows idx]))]
    [:input (cond-> {:type "checkbox"
                     :checked checked?
                     :on-change #(deframe/route-assoc! ::selected-rows (if checked?
                                                                         (disj @!selected-rows idx)
                                                                         (conj @!selected-rows idx)))}
              vin (assoc :data-testid (str "row-" vin)))]))

(comment
  (count @!selected-rows))

(defn extract-vin
  "Terrible hack to extract the vin to fix the ui tests. The amount of
  hoops we're jumping through in this namespace because we're using
  clerk tables with a representation that loses the column names is
  not pretty."
  [wrapped-value]
  (let [maybe-html-link (:nextjournal/value wrapped-value)]
    (when (vector? maybe-html-link)
      (hiccup->text (peek maybe-html-link)))))

(defn filter-table-columns [{:keys [table num]} {:keys [column-order sticky-cols-count]}]
  (let [{:keys [head rows columns]} table
        column->index (zipmap columns (range (count columns)))
        ;; Use column-order if provided (from profile), otherwise sort by original index
        selected-column-indices (if (seq column-order)
                                  (keep column->index column-order)
                                  (-> column->index
                                      (select-keys @!selected-columns)
                                      vals
                                      sort))
        row->selected-column-values (fn row->selected-column-values [row] (map (partial nth row) selected-column-indices))
        selected-head (row->selected-column-values head)
        vin-index (count (take-while (complement #{:vehicle/vin})
                                     selected-head))]
    {:head (cons (viewer/with-viewer {:render-fn (partial head-checkbox num)} :head)
                 selected-head)
     :rows (map-indexed (fn filter-table-column-map [idx row]
                          (let [selected-row (row->selected-column-values row)]
                            (cons (viewer/with-viewer {:render-fn row-checkbox
                                                       :transform-fn viewer/mark-presented}
                                    {:idx idx
                                     :vin (extract-vin (first (drop vin-index selected-row)))})
                                  selected-row)))
                        rows)
     :sticky-cols-count (when sticky-cols-count (inc sticky-cols-count))}))

(defn inst->compound-time-str-viewer [opts]
  (let [opts+zone (cond-> opts
                    (contains? opts :compound/name)
                    (assoc :zone (compound-config/timezone-at (:compound/name opts))))]
    (assoc dcv/inst->str-viewer :transform-fn (viewer/update-val (partial time-format/->str opts+zone)))))

(defn results-table [params results]
  (let [col-settings (effective-col-settings)
        result-columns (-> results :table :columns)
        ;; Only use profile system if user has saved profiles, otherwise use existing behavior
        col-opts (when col-settings
                   (columns/->table-opts {:col-settings col-settings
                                          :available-cols (available-columns result-columns)
                                          :default-selected-cols (default-selected-cols-ordered result-columns)}))]
    [render/inspect (viewer/table {:nextjournal.clerk/viewers
                                   (viewer/add-viewers [(inst->compound-time-str-viewer params)
                                                        dcv/uuid->str-viewer
                                                        dcv/boolean-table-cell->yes-no-viewer
                                                        dcv/transport-mode->str-viewer
                                                        (dcv/namespaced-keyword->str-viewer "external.pegaso.status")
                                                        (dcv/namespaced-keyword->str-viewer "parking-spot.status")
                                                        dcv/blocks-viewer
                                                        dcv/labels-viewer
                                                        dcv/list-of-strings-viewer
                                                        dcv/comments-viewer
                                                        dcv/translated-keywords-viewer
                                                        dcv/render-fn-explain-viewer])
                                   :nextjournal.clerk/page-size nil
                                   :nextjournal.clerk/budget nil
                                   :nextjournal.clerk/render-opts {:translated-keys {:head ""}}}
                                  (filter-table-columns results col-opts))]))

(defn export-handler [_event]
  (http/download "/api/lambda"
                 {:lambda/handlers '[ductile.vehicle-search/use-case-search
                                     ductile.spreadsheet/export-xlsx]
                  :search-params (merge (select-keys (:path-params @deframe/!current-route) [:compound/name])
                                        (dissoc @!form-params :selected-columns))
                  :ductile.vehicle-search/use-case (:use-case-key @!use-case)
                  :export? true
                  :result-keys (or (when-let [result-columns (-> @!results :result :table :columns)]
                                     (selected-columns-from-profile result-columns))
                                   (selected-columns))}))

(def block-types
  [:block/by-compound
   :block/by-customer])

(defn manage-block! [action block-type]
  (when (js/confirm (case action
                      :block/add (tr "Are you sure you want to block %1 vehicles (%2)?"
                                     (count @!selected-rows)
                                     (tr block-type))
                      :block/remove (tr "Are you sure you want to unblock %1 vehicles (%2)?"
                                        (count @!selected-rows)
                                        (tr block-type))))
    (deframe/call-lambda
     {:on-result (fn [result]
                   (if (:error result)
                     (deframe/route-assoc! ::flash-notice
                                           {:type :error :message (:error-message result)})
                     (do
                       (deframe/assoc! :toast
                                       {:title (str "✅ " (case action
                                                           :block/add (tr "Block successful")
                                                           :block/remove (tr "Unblock successful")))
                                        :description (case action
                                                       :block/add (tr "The selected vehicles are now blocked.")
                                                       :block/remove (tr "The selected vehicles are now unblocked."))
                                        :variant :success})
                       (deframe/dispatch! `search (merge
                                                   (select-keys @deframe/!current-route [:path-params])
                                                   {:form-params @!form-params})))))}
     'ductile.workflow.engine/handle-manage-blocks
     {:action action
      :block-type block-type
      :compound-name (get-in @deframe/!current-route [:path-params :compound/name])
      :ductile.vehicle-search/use-case (:use-case-key @!use-case)
      :vehicle-search-params {:search-params (dissoc (search-params-with-row-selection) :selected-columns)
                              :basis-t (get-in @!results [:result :basis-t])}})))

(defn manage-block-dropdown [action]
  (into
   [:> DropdownMenu/SubContent
    {:class "min-w-[220px] bg-white rounded-md p-[5px] shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
     :side-offset 2
     :align-offset -5}]
   (map (fn [block-type]
          [:> DropdownMenu/Item
           {:class-name "group text-[13px] leading-none text-indigo-700 rounded-[3px] flex items-center h-[25px] px-[5px] relative pl-[25px] select-none outline-none data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none data-[highlighted]:bg-indigo-500 data-[highlighted]:text-white"
            :on-select #(manage-block! action block-type)}
           (tr block-type)]))
   block-types))

(defn actions-dropdown [_table _params]
  [:> DropdownMenu/Root
   [:> DropdownMenu/Trigger
    [ui/button
     {:variant :white}
     (tr "Actions")
     [ui/icon :chevron-down]]]
   [:> DropdownMenu/Portal
    [:> DropdownMenu/Content
     {:class-name "min-w-[220px] bg-white rounded-md p-[5px] shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
      :side-offset 7}
     [:> DropdownMenu/Arrow {:class "fill-white"}]
     [:> DropdownMenu/Sub
      [:> DropdownMenu/SubTrigger
       {:class "group text-[13px] leading-none text-indigo-700 rounded-[3px] flex items-center h-[25px] px-[5px] relative pl-[25px] select-none outline-none data-[state=open]:bg-indigo-100 data-[state=open]:text-indigo-700 data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none data-[highlighted]:bg-indigo-500 data-[highlighted]:text-white data-[highlighted]:data-[state=open]:bg-indigo-500 data-[highlighted]:data-[state=open]:text-white"}
       (tr "Block selected vehicles")
       [:div {:class "ml-auto pl-[20px] text-mauve11 group-data-[highlighted]:text-white group-data-[disabled]:text-mauve8"}
        [ui/icon :chevron-right]]]
      [:> DropdownMenu/Portal
       [manage-block-dropdown :block/add]]]
     [:> DropdownMenu/Sub
      [:> DropdownMenu/SubTrigger
       {:class "group text-[13px] leading-none text-indigo-700 rounded-[3px] flex items-center h-[25px] px-[5px] relative pl-[25px] select-none outline-none data-[state=open]:bg-indigo-100 data-[state=open]:text-indigo-700 data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none data-[highlighted]:bg-indigo-500 data-[highlighted]:text-white data-[highlighted]:data-[state=open]:bg-indigo-500 data-[highlighted]:data-[state=open]:text-white"}
       (tr "Unblock selected vehicles")
       [:div {:class "ml-auto pl-[20px] text-mauve11 group-data-[highlighted]:text-white group-data-[disabled]:text-mauve8"}
        [ui/icon :chevron-right]]]
      [:> DropdownMenu/Portal
       [manage-block-dropdown :block/remove]]]
     [:> DropdownMenu/Item
      {:class-name "group text-[13px] leading-none text-indigo-700 rounded-[3px] flex items-center h-[25px] px-[5px] relative pl-[25px] select-none outline-none data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none data-[highlighted]:bg-indigo-500 data-[highlighted]:text-white"
       :on-select (fn [] (ui/show-form-modal! 'ductile.vehicle-search.bulk-actions/show-bulk-order-service-form
                                              {:vehicle-search-params {:search-params (search-params-with-row-selection)
                                                                       :basis-t (get-in @!results [:result :basis-t])}}))}
      (tr "Order service for selected vehicles")]
     [:> DropdownMenu/Item
      {:class-name "group text-[13px] leading-none text-indigo-700 rounded-[3px] flex items-center h-[25px] px-[5px] relative pl-[25px] select-none outline-none data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none data-[highlighted]:bg-indigo-500 data-[highlighted]:text-white"
       :on-select (fn [] (ui/show-form-modal! 'ductile.vehicle-search.bulk-actions/show-bulk-service-perform-form
                                              {:vehicle-search-params {:search-params (search-params-with-row-selection)
                                                                       :basis-t (get-in @!results [:result :basis-t])}}))}
      (tr "Perform service for selected vehicles")]
     [:> DropdownMenu/Item
      {:class-name "group text-[13px] leading-none text-indigo-700 rounded-[3px] flex items-center h-[25px] px-[5px] relative pl-[25px] select-none outline-none data-[disabled]:text-mauve8 data-[disabled]:pointer-events-none data-[highlighted]:bg-indigo-500 data-[highlighted]:text-white"
       :on-select (fn [] (ui/show-form-modal! 'ductile.vehicle-search.bulk-actions/show-bulk-generate-vin-label-form
                                              {:vehicle-search-params {:search-params (search-params-with-row-selection)
                                                                       :basis-t (get-in @!results [:result :basis-t])}}))}
      (tr "Generate VIN labels for selected vehicles")]]]])

(defn export-button []
  (let [export-running? (r/atom false)]
    (fn []
      [ui/button
       {:disabled? @export-running?
        :variant :white
        :on-click (fn [e]
                    (reset! export-running? true)
                    (-> (export-handler e)
                        (.then #(reset! export-running? false))))}
       icons/excel
       (tr "Export")])))

(defn search-results [results]
  (if (zero? (:num results))
    [:div.mb-3 {:class "min-h-[42px]"}
     [:h1.text-xl.pl-4 (tr "No vehicles found")]
     (when-let [flash-notice @(deframe/!route-get ::flash-notice)]
       [:div.mb-3
        [:div.rounded-lg.overflow-hidden
         [ui/flash-notice (assoc flash-notice :on-close #(deframe/route-dissoc! ::flash-notice))]]])]
    [:div
     [:div
      [:div.flex.justify-between.gap-3.items-center.mb-3
       {:class "min-h-[42px]"}
       [:div.flex.flex-col
        [:div.flex.items-center
         [:h2.text-lg.pl-4.flex.items-center.flex-wrap.mt-0.gap-2
          [:span (cond-> {:data-testid "vehicle-search-results-count"}
                   (:loading @!results) (assoc :data-search-loading true))
           (trn ["%1 vehicle" "%1 vehicles"] (:num results))]

          [:span.text-slate-600.font-normal.text-base {:data-testid "selection-count-text"}
           (cond
             (= (count @!selected-rows) (:num results)) (tr "All")
             (empty? @!selected-rows) (tr "None")
             :else (count @!selected-rows)) " " (tr "selected")]
          (when (and (seq @!selected-rows)
                     (not (results-elided? results)))
            [ui/button {:variant :white
                        :on-click #(let [vins (str/join "\n" (selected-row-vins results @!selected-rows))]
                                     (.writeText js/navigator.clipboard vins))}
             [ui/icon :copy]
             (tr "copy VINs")])]]
        (when (> (:num results) default-result-limit)
          [:span.pl-4.text-slate-600.text-xs (tr "showing the first %1 results" default-result-limit)])]
       [:div.flex.gap-3
        [columns/select-profile-dropdown
         {:col-settings (effective-col-settings)
          :on-change on-col-settings-change!
          :on-modal-open #(deframe/route-assoc! ::col-settings-modal? true)}]
        (let [{:keys [form export-not-elided? show-export? hide-action-dropdown? use-case-key]} @!use-case
              action (get-in use-case-data [use-case-key :action])]
          (cond
            form [:<>]

            (and action (or show-export? (and export-not-elided? (not (results-elided? results)))))
            [:div.flex.justify-end.gap-3.flex-wrap-reverse
             [:<> [export-button] [action]]]

            action [action]

            :else [:<>
                   (when-not hide-action-dropdown? [actions-dropdown (:table results) @!form-params])
                   [export-button]]))
        [ui/button
         {:on-click #(deframe/route-assoc! ::search-collapsed? (not @(deframe/!route-get ::search-collapsed?)))
          :variant :white}
         (if @(deframe/!route-get ::search-collapsed?)
           [ui/icon {:size 16} :minimize-2]
           [ui/icon {:size 16} :maximize-2])]]]]
     [:div.flex.flex-col.overflow-y-auto
      {:style {:max-height "calc(100vh - 130px - 22px)"}}
      (when-let [flash-notice @(deframe/!route-get ::flash-notice)]
        [:div.mb-3
         [:div.rounded-lg.overflow-hidden
          [ui/flash-notice (assoc flash-notice :on-close #(deframe/route-dissoc! ::flash-notice))]]])
      (when-let [form (get-in use-case-data [(:use-case-key @!use-case) :form])]
        [form])
      [results-table (select-keys @!form-params [:compound/name]) results]
      (when @(deframe/!route-get ::col-settings-modal?)
        [columns/select-columns-modal
         {:col-settings (effective-col-settings)
          :available-cols (available-columns (-> results :table :columns))
          :default-selected-cols (default-selected-cols-ordered (-> results :table :columns))
          :enable-sticky-cols? false
          :on-close #(deframe/route-dissoc! ::col-settings-modal?)
          :on-change (fn [new-col-settings]
                       (on-col-settings-change! new-col-settings)
                       (deframe/route-dissoc! ::col-settings-modal?))}])]]))

(defmethod deframe/route-on-fetch ::on-fetch [{:as match :keys [data path-params query-params]}]
  (let [on-load-search-params @(deframe/!get ::on-load-search-params)
        form-params (or on-load-search-params
                        (parse-query-params query-params))]
    (deframe/route-assoc! ::form-params form-params)
    ;; clear after accessing so navigating to another vehicle-search page doesn't apply these search params
    (deframe/dissoc! ::on-load-search-params)

    {:build-lambda-params (fn [match]
                            (let [params (deframe/build-lambda-params match)]
                              (merge params
                                     (select-keys (:data match) [:ductile.vehicle-search/use-case])
                                     (parse-query-params params)
                                     ;; params to inject on initial page load, side-steps url length constraints
                                     on-load-search-params)))
     :on-result (fn [result]
                  (deframe/route-assoc! ::search-results (set/rename-keys result {:search-results :result}))
                  (deframe/route-assoc! ::option-results (dissoc result :search-results))
                  (deframe/route-assoc! ::use-case (:ductile.vehicle-search/use-case result))
                  (deframe/dispatch-effects! {`query-params->url! (prepare-search-params form-params)})
                  (when-not (:error result)
                    (deframe/route-assoc! ::selected-rows (if (:rows-preselected? (:ductile.vehicle-search/use-case result) true)
                                                            (set (range 0 (-> result :search-results :num)))
                                                            #{}))))}))

(defn render-vehicle-search [_result {:as match :keys [data]}]
  (let [!search-collapsed? (deframe/!route-get ::search-collapsed?)]
    [:div
     {:class (when-not @!search-collapsed? "flex gap-2")}
     (when-not @!search-collapsed?
       [:div.flex-shrink-0 {:class "w-[550px]"}
        [search-form (cond-> {:option-values @(deframe/!route-get ::option-results)}
                       (:title @!use-case)
                       (assoc :title (:title @!use-case)))]])
     (let [{:keys [loading error result]} @!results]
       (cond
         loading [ui/loading-spinner]
         error [ui/error-view error]
         result [:div.flex-auto.overflow-auto
                 [search-results result]]))]))
